import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { generateGLBThumbnail } from '../../utils/glbThumbnailGenerator';
import { getThumbnailFromCache, saveThumbnailToCache } from '../../utils/glbThumbnailCache';

const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
`;

const breakpointsX = { mobile: '768px', tablet: '1024px' };

// === 새 디자인 셸 styled 컴포넌트 ===
const NewPage = styled.div`
  padding: 1.25rem 1.5rem 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: 1600px;

  @media (max-width: ${breakpointsX.mobile}) {
    padding: 1rem;
  }
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
  font-size: 0.88rem;
  margin-bottom: 0.25rem;

  & svg.home { width: 16px; height: 16px; color: #6b7280; }
  & .sep { color: #cbd5e1; }
`;

const CrumbBtn = styled.button`
  background: none;
  border: none;
  color: ${({ active }) => (active ? '#1d4ed8' : '#6b7280')};
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;

  &:hover { color: #1d4ed8; background: #eff6ff; }
`;

const Toolbar = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.7rem 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const SelectionLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.7rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  user-select: none;

  & input { accent-color: #2563eb; cursor: pointer; }
  &:hover { background: #f9fafb; border-color: #d1d5db; }
`;

const ToolBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.12s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: #2563eb;
    color: #1d4ed8;
    background: #eff6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  & svg { width: 14px; height: 14px; }

  ${({ tone }) =>
    tone === 'danger' &&
    `
    color: #b91c1c;
    border-color: #fecaca;
    background: #fff5f5;
    &:hover:not(:disabled) { background: #fee2e2; border-color: #fca5a5; color: #991b1b; }
  `}
  ${({ tone }) =>
    tone === 'primary' &&
    `
    color: #ffffff;
    background: #2563eb;
    border-color: #2563eb;
    &:hover:not(:disabled) { background: #1d4ed8; color: #ffffff; }
  `}
`;

const ViewBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 0.15rem;
`;

const ViewToggleGroup = styled.div`
  display: flex;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 2px;
`;

const ViewToggleBtn = styled.button`
  width: 32px;
  height: 30px;
  border: none;
  background: ${({ active }) => (active ? '#2563eb' : 'transparent')};
  color: ${({ active }) => (active ? '#ffffff' : '#9ca3af')};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover {
    color: ${({ active }) => (active ? '#ffffff' : '#374151')};
    background: ${({ active }) => (active ? '#2563eb' : '#f3f4f6')};
  }

  & svg { width: 14px; height: 14px; }
`;

const TableCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
`;

const TableEl = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: 0.78rem;
  font-weight: 600;
  color: #6b7280;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'default')};
  user-select: none;
  background: #fafbfc;

  & .arrow {
    font-size: 0.7rem;
    margin-left: 0.2rem;
    color: ${({ active }) => (active ? '#2563eb' : '#cbd5e1')};
  }

  &:first-child { width: 44px; padding-left: 1.1rem; }
  &:last-child { text-align: right; padding-right: 1.1rem; width: 110px; }
`;

const Td = styled.td`
  padding: 0.65rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.85rem;
  color: #374151;
  vertical-align: middle;

  &:first-child { padding-left: 1.1rem; width: 44px; }
  &:last-child { text-align: right; padding-right: 1.1rem; }

  tr:last-child & { border-bottom: none; }
`;

const RowTr = styled.tr`
  transition: background 0.1s ease;
  animation: ${fadeInUp} 0.18s ease both;

  &:hover { background: #fafbfc; }
`;

const SkeletonTr = styled.tr`
  animation: ${fadeInUp} 0.18s ease both;
`;

const Shimmer = styled.span`
  display: inline-block;
  height: ${({ h }) => h || '12px'};
  width: ${({ w }) => w || '100%'};
  background: linear-gradient(
    90deg,
    #f1f5f9 0%,
    #e2e8f0 35%,
    #f1f5f9 70%
  );
  background-size: 600px 100%;
  border-radius: 4px;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const ShimmerBox = styled.div`
  width: ${({ size }) => size || '36px'};
  height: ${({ size }) => size || '36px'};
  border-radius: 8px;
  background: linear-gradient(
    90deg,
    #f1f5f9 0%,
    #e2e8f0 35%,
    #f1f5f9 70%
  );
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
`;

const SpinnerEl = styled.span`
  display: inline-block;
  width: ${({ size }) => size || '16px'};
  height: ${({ size }) => size || '16px'};
  border: 2px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  vertical-align: middle;
`;

const LoadingBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.95rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  color: #1d4ed8;
  font-size: 0.82rem;
  font-weight: 600;
  animation: ${pulse} 1.6s ease-in-out infinite;
`;

const BigSpinner = styled.div`
  width: 56px;
  height: 56px;
  border: 4px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ${spin} 0.85s linear infinite;
`;

const LoadingHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 4rem 2rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  min-height: 320px;
  animation: ${fadeInUp} 0.2s ease both;
`;

const LoadingHeroTitle = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
`;

const LoadingHeroSub = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  text-align: center;
  line-height: 1.5;
`;

const TopProgress = styled.div`
  position: relative;
  height: 2px;
  background: transparent;
  overflow: hidden;
  margin: -0.5rem 0 0.5rem;

  &::before {
    content: '';
    position: absolute;
    left: -40%;
    top: 0;
    height: 100%;
    width: 40%;
    background: linear-gradient(90deg, transparent, #2563eb, transparent);
    animation: ${keyframes`
      0% { left: -40%; }
      100% { left: 100%; }
    `} 1.1s ease-in-out infinite;
  }
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  min-width: 0;
`;

const FileThumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${({ bg }) => bg || '#f3f4f6'};
  color: ${({ color }) => color || '#374151'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
  overflow: hidden;

  & img { width: 100%; height: 100%; object-fit: cover; }
  & svg { width: 18px; height: 18px; }
`;

const FileNameInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
`;

const FileNameText = styled.span`
  font-weight: 600;
  color: #111827;
  font-size: 0.88rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;
`;

const FileUrlText = styled.a`
  font-size: 0.72rem;
  color: #2563eb;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 360px;

  &:hover { text-decoration: underline; }
`;

const SourcePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${({ tone }) =>
    tone === 'success' ? '#dcfce7' : tone === 'warn' ? '#fef3c7' : '#f3f4f6'};
  color: ${({ tone }) =>
    tone === 'success' ? '#166534' : tone === 'warn' ? '#92400e' : '#6b7280'};
  margin-right: 0.35rem;
  white-space: nowrap;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.3rem;
  justify-content: flex-end;
`;

const IconActionBtn = styled.button`
  width: 30px;
  height: 30px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #4b5563;
  transition: all 0.12s ease;

  &:hover { border-color: #2563eb; color: #1d4ed8; background: #eff6ff; }
  & svg { width: 14px; height: 14px; }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1.1rem;
  border-top: 1px solid #f3f4f6;
  background: #ffffff;
  flex-wrap: wrap;
  gap: 0.5rem;
  font-size: 0.82rem;
`;

const PageInfo = styled.div`
  color: #6b7280;
`;

const PageNums = styled.div`
  display: flex;
  align-items: center;
  gap: 0.2rem;
`;

const PageNumBtn = styled.button`
  min-width: 30px;
  height: 30px;
  padding: 0 0.5rem;
  border: 1px solid ${({ active }) => (active ? '#2563eb' : '#e5e7eb')};
  background: ${({ active }) => (active ? '#2563eb' : '#ffffff')};
  color: ${({ active }) => (active ? '#ffffff' : '#374151')};
  border-radius: 7px;
  font-size: 0.82rem;
  font-weight: ${({ active }) => (active ? 700 : 500)};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ active }) => (active ? '#1d4ed8' : '#f3f4f6')};
    border-color: ${({ active }) => (active ? '#1d4ed8' : '#d1d5db')};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const PageNavBtn = styled(PageNumBtn)`
  & svg { width: 12px; height: 12px; }
`;

const PerPageSelect = styled.select`
  padding: 0.4rem 1.6rem 0.4rem 0.65rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 0.55rem center;
  background-size: 12px 12px;
`;

const Empty2 = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.9rem;
`;

const Ellipsis = styled.span`
  padding: 0 0.4rem;
  color: #9ca3af;
`;

// === 새 디자인 아이콘들 ===
const HomeSvg = () => (
  <svg className="home" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-7H9v7H4a1 1 0 0 1-1-1V9.5z" />
  </svg>
);

const DownloadSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const ChevronDownSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const MoreSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

const FolderYellowSvg = () => (
  <svg viewBox="0 0 24 24" fill="#FBBF24">
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
  </svg>
);

const PdfSvg = () => (
  <svg viewBox="0 0 24 24" fill="#EF4444"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#fff">PDF</text></svg>
);

const DocSvg = () => (
  <svg viewBox="0 0 24 24" fill="#2563EB"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#fff">DOC</text></svg>
);

const SheetSvg = () => (
  <svg viewBox="0 0 24 24" fill="#16A34A"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#fff">XLS</text></svg>
);

const ZipSvg = () => (
  <svg viewBox="0 0 24 24" fill="#F59E0B"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="bold" fill="#fff">ZIP</text></svg>
);

const Generic3DSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const GenericFileSvg = () => (
  <svg viewBox="0 0 24 24" fill="#9CA3AF"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /></svg>
);

const ChevronLeftSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRightSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const PageLayout = styled.div`
  display: grid;
  grid-template-columns: 350px 1fr;
  gap: 2rem;
  max-width: 1600px;
  width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Panel = styled.div`
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #eaeaea;
  height: calc(100vh - 120px);
  overflow-y: auto;
`;

const PanelTitle = styled.h3`
  margin: 0;
  padding: 1rem;
  border-bottom: 1px solid #eaeaea;
  font-size: 1rem;
  color: #333;
`;

const ContentWrapper = styled.div`
  padding: 1rem;
`;

const FolderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FolderItem = styled.div`
  padding: 0.5rem;
  cursor: pointer;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  background: ${(props) => (props.selected ? '#dbeafe' : '#ffffff')};
  &:hover {
    background: #f1f5f9;
  }
`;

const FileList = styled.div`
  display: grid;
  grid-template-rows: repeat(auto-fill, minmax(50px, 1fr));
  gap: 1rem;
`;

const FileCard = styled.div`
  background: #f8fafc;
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 1rem;
  cursor: pointer;
  max-width: 700px;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    transform: translateY(-2px);
  }
`;

const FileCardContent = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

const ThumbnailContainer = styled.div`
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FileIcon = styled.div`
  font-size: 2rem;
  color: #9ca3af;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const FileDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileUrl = styled.div`
  font-weight: 100;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileInfo = styled.div`
  font-size: 0.75rem;
  color: #666;
  display: flex;
  justify-content: space-between;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SortButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  border-radius: 4px;
  background: ${props => props.active ? '#eff6ff' : '#ffffff'};
  color: ${props => props.active ? '#3b82f6' : '#6b7280'};
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: ${props => props.active ? '#dbeafe' : '#f9fafb'};
  }
`;

const ActionButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  background: ${props => props.primary ? '#3b82f6' : '#ffffff'};
  color: ${props => props.primary ? '#ffffff' : '#3b82f6'};
  cursor: pointer;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#eff6ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DownloadButton = styled.button`
  padding: 0.25rem 0.5rem;
  border: 1px solid #10b981;
  border-radius: 4px;
  background: #10b981;
  color: white;
  cursor: pointer;
  font-size: 0.75rem;

  &:hover {
    background: #059669;
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const FileCardWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #eaeaea;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }
`;

const MetadataRow = styled.div`
  display: flex;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const MetadataLabel = styled.div`
  font-weight: 600;
  color: #374151;
  min-width: 150px;
  font-size: 0.875rem;
`;

const MetadataValue = styled.div`
  color: #6b7280;
  flex: 1;
  font-size: 0.875rem;
  word-break: break-all;
`;

const ThumbnailPreview = styled.img`
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  margin: 1rem 0;
  border-radius: 8px;
  background: #f5f5f5;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const ProgressContainer = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const ProgressText = styled.div`
  text-align: center;
  font-size: 0.875rem;
  color: #0369a1;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 24px;
  background: #e0f2fe;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: ${props =>
    props.status === 'error' ? '#ef4444' :
    props.status === 'completed' ? '#22c55e' :
    'linear-gradient(90deg, #0284c7 0%, #0ea5e9 100%)'};
  transition: width 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
`;

const SplitList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SplitItem = styled.div`
  border: 1px solid #eaeaea;
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;

  &:hover {
    background: #f1f5f9;
  }
`;

const SplitInfo = styled.div`
  flex: 1;
`;

const SplitTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.25rem;
  color: #333;
`;

const SplitDetails = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #333;
`;

const InfoValue = styled.span`
  color: #666;
`;

const SearchContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.5rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FileSource = styled.div`
  font-size: 0.75rem;
  color: #3b82f6;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #eff6ff;
  border-radius: 4px;
  display: inline-block;
  font-weight: 500;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
`;

const LoadMoreButton = styled.button`
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const ToggleButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.active ? '#10b981' : '#6b7280'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  min-width: 2.5rem;
  background: ${props => props.active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#374151'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumberContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const ViewModeButton = styled.button`
  padding: 0.5rem;
  background: ${props => props.active ? '#3b82f6' : '#ffffff'};
  color: ${props => props.active ? '#ffffff' : '#6b7280'};
  border: 1px solid ${props => props.active ? '#3b82f6' : '#d1d5db'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  padding: 1rem 0;
`;

const GridItem = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const GridThumbnail = styled.div`
  width: 100%;
  height: 200px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const GridThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const GridInfo = styled.div`
  padding: 0.75rem;
`;

const GridFileName = styled.div`
  font-weight: 500;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GridMeta = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const LoadingSpinner = styled.div`
  font-size: ${props => props.size || '3rem'};
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const FileView = ({
  selectedRemote,
  currentPath: externalCurrentPath,
  setCurrentPath: externalSetCurrentPath,
  searchValue: externalSearchValue,
}) => {
  // 서버 타입에 따라 API 경로 결정
  const getApiBase = () => {
    if (!selectedRemote) return '/awsUpload';
    if (selectedRemote.type === 'HETZNER_S3') return '/hetzner';
    if (selectedRemote.type === 'CLOUDFLARE_R2') return '/r2';
    return '/awsUpload';
  };
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [internalCurrentPath, setInternalCurrentPath] = useState('');
  const currentPath = externalCurrentPath !== undefined ? externalCurrentPath : internalCurrentPath;
  const setCurrentPath = externalSetCurrentPath || setInternalCurrentPath;
  const [error, setError] = useState(null);
  const [folderSize, setFolderSize] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSplitDownload, setShowSplitDownload] = useState(false);
  const [splitInfo, setSplitInfo] = useState(null);
  const [sortBy, setSortBy] = useState(null); // null, name, date, size (기본: 정렬 안함)
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [downloadProgress, setDownloadProgress] = useState(null); // { current: 1, total: 5, status: 'downloading' }
  const [internalSearchQuery, setSearchQuery] = useState(''); // 검색어
  const searchQuery = externalSearchValue !== undefined ? externalSearchValue : internalSearchQuery;
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true); // 페이징 활성화 여부 (true=페이지번호, false=더보기)
  const [currentPage, setCurrentPage] = useState(1); // 현재 페이지
  const [displayedFileCount, setDisplayedFileCount] = useState(50); // 더보기 모드에서 표시할 파일 개수
  const ITEMS_PER_PAGE = 50; // 한 페이지당 파일 개수
  const [glbThumbnails, setGlbThumbnails] = useState({}); // GLB 파일 썸네일 캐시 { fileKey: thumbnailDataUrl }
  const [loadingThumbnails, setLoadingThumbnails] = useState(new Set()); // 현재 로딩 중인 썸네일
  const [metadataModalOpen, setMetadataModalOpen] = useState(false); // 메타데이터 모달 열림 상태
  const [selectedFileMetadata, setSelectedFileMetadata] = useState(null); // 선택된 파일 메타데이터
  const [viewMode, setViewMode] = useState('list'); // 'list' 또는 'grid'
  const [isLoadingFiles, setIsLoadingFiles] = useState(false); // 파일 리스트 로딩 중
  const [failedImages, setFailedImages] = useState(new Set()); // 로딩 실패한 이미지 URL

  // 검색어나 정렬이 변경되면 페이징 초기화
  useEffect(() => {
    setCurrentPage(1);
    setDisplayedFileCount(ITEMS_PER_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, sortBy, sortOrder]);

  // raw 파일 리스트 (드라이브 단위로 한 번만 fetch, currentPath 변경 시 재파싱만)
  const [rawFiles, setRawFiles] = useState([]);

  // 드라이브 변경 시: sessionStorage 캐시 확인 → 없으면 fetch
  useEffect(() => {
    if (!selectedRemote) {
      setRawFiles([]);
      return;
    }

    const cacheKey = `antsnest.list.${selectedRemote.name}`;
    const TTL_MS = 60 * 1000; // 60초

    // 즉시 캐시에서 시도
    let usedCache = false;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed.ts && Date.now() - parsed.ts < TTL_MS && Array.isArray(parsed.files)) {
          setRawFiles(parsed.files);
          setError(null);
          usedCache = true;
        }
      }
    } catch {}

    const fetchFiles = async () => {
      if (!usedCache) setIsLoadingFiles(true);
      try {
        const response = await fetch(selectedRemote.fileList);
        if (!response.ok) throw new Error(`Failed to fetch files: ${response.status}`);
        const data = await response.json();
        const files = data.files || [];
        setRawFiles(files);
        setError(null);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), files }));
        } catch {}
      } catch (err) {
        console.error('Error fetching files:', err);
        if (!usedCache) setError(err.message);
      } finally {
        setIsLoadingFiles(false);
      }
    };

    fetchFiles();
  }, [selectedRemote]);

  // 메타데이터 캐시 (key → metadata)
  const [metaCache, setMetaCache] = useState({});
  const inflightMetaRef = useRef(new Set());

  // 현재 페이지에 보이는 파일들의 메타데이터 lazy fetch
  useEffect(() => {
    if (!selectedRemote || rawFiles.length === 0) return;
    // 페이지에 보이는 키만 추려냄
    const pathPrefix = currentPath ? `${currentPath}/` : '';
    const visibleKeys = rawFiles
      .filter((f) => {
        if (!f.key.startsWith(pathPrefix)) return false;
        const rel = f.key.slice(pathPrefix.length);
        return !rel.includes('/');
      })
      .map((f) => f.key);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const pageKeys = visibleKeys.slice(start, start + ITEMS_PER_PAGE);
    const missing = pageKeys.filter((k) => !metaCache[k] && !inflightMetaRef.current.has(k));
    if (missing.length === 0) return;
    missing.forEach((k) => inflightMetaRef.current.add(k));

    const base = getApiBase();
    fetch(`${base}/list-meta`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ keys: missing }),
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data?.metadata) {
          setMetaCache((prev) => ({ ...prev, ...data.metadata }));
        }
      })
      .catch(() => {})
      .finally(() => {
        missing.forEach((k) => inflightMetaRef.current.delete(k));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawFiles, currentPath, currentPage, selectedRemote]);

  // raw 또는 currentPath 변경 시: 클라이언트 측 재파싱 (네트워크 X)
  useEffect(() => {
    if (!selectedRemote) return;
    const { parsedFolders, parsedFiles, totalSize } = parseStructure(rawFiles, currentPath, selectedRemote.address);
    // 캐시된 메타데이터 머지
    const withMeta = parsedFiles.map((f) => ({
      ...f,
      metadata: metaCache[f.key] || f.metadata || {},
    }));
    setFolders(parsedFolders);
    setFiles(withMeta);
    setFolderSize(totalSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawFiles, currentPath, selectedRemote, metaCache]);
 
  const parseStructure = (awsFiles) => {
    const parsedFolders = new Set();
    const parsedFiles = [];
    let totalSize = 0;
    const pathPrefix = currentPath ? `${currentPath}/` : '';

    awsFiles.forEach(file => {
      const relativePath = file.key.slice(pathPrefix.length);
      const parts = relativePath.split('/');

      if (file.key.startsWith(pathPrefix)) {
        if (parts.length > 1) {
          // 현재 경로의 직계 하위 폴더 추가
          parsedFolders.add(parts[0]);
        } else if (parts.length === 1 && parts[0]) {
          // 현재 경로의 파일 추가
          parsedFiles.push({
            key: file.key, // S3 키 추가
            name: parts[0],
            url: `${selectedRemote.address}/${file.key}`,
            size: file.size,
            createdAt: file.lastModified,
            metadata: file.metadata || {} // 메타데이터 추가
          });
          totalSize += file.size;
        }
      }
    });

    return {
      parsedFolders: Array.from(parsedFolders),
      parsedFiles,
      totalSize
    };
  };

  const handleFolderClick = (folder) => {
    setSelectedFiles([]); // 폴더 이동 시 선택 초기화
    setCurrentPath(currentPath ? `${currentPath}/${folder}` : folder);
  };

  const handleBackClick = () => {
    setSelectedFiles([]); // 폴더 이동 시 선택 초기화
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const formatSize = (sizeInBytes) => {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = sizeInBytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size?.toFixed(2)} ${units[unitIndex]}`;
  };

  // 이미지 파일 확인
  const isImageFile = (filename) => {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.ico'];
    return imageExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  // 3D 모델 파일 확인
  const is3DModelFile = (filename) => {
    const modelExtensions = ['.glb', '.gltf', '.obj', '.fbx', '.stl', '.dae'];
    return modelExtensions.some(ext => filename.toLowerCase().endsWith(ext));
  };

  // GLB 파일 확인 (썸네일 생성 가능한 파일)
  const isGLBFile = (filename) => {
    return filename.toLowerCase().endsWith('.glb') || filename.toLowerCase().endsWith('.gltf');
  };

  // GLB 썸네일 로드
  const loadGLBThumbnail = useCallback(async (file) => {
    const fileKey = file.key;

    // 이미 로드 중이거나 이미 로드된 경우 스킵
    if (loadingThumbnails.has(fileKey) || glbThumbnails[fileKey]) {
      return;
    }

    // 로딩 상태 추가
    setLoadingThumbnails(prev => new Set(prev).add(fileKey));

    try {
      // 캐시에서 먼저 확인
      const cached = await getThumbnailFromCache(fileKey, file.lastModified);
      if (cached) {
        setGlbThumbnails(prev => ({ ...prev, [fileKey]: cached }));
        setLoadingThumbnails(prev => {
          const next = new Set(prev);
          next.delete(fileKey);
          return next;
        });
        return;
      }

      // 썸네일 생성 (크기를 300으로 증가)
      const thumbnailData = await generateGLBThumbnail(file.url, 300);

      // 썸네일 저장
      setGlbThumbnails(prev => ({ ...prev, [fileKey]: thumbnailData }));

      // 캐시에 저장
      await saveThumbnailToCache(fileKey, thumbnailData, file.lastModified);

      // 로딩 상태 제거
      setLoadingThumbnails(prev => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
    } catch (error) {
      console.error(`GLB 썸네일 생성 실패: ${fileKey}`, error);
      // 로딩 상태 제거
      setLoadingThumbnails(prev => {
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
    }
  }, [glbThumbnails, loadingThumbnails]);

  // 파일 타입에 따른 아이콘
  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase();
    if (ext.endsWith('.pdf')) return '📄';
    if (ext.endsWith('.doc') || ext.endsWith('.docx')) return '📝';
    if (ext.endsWith('.xls') || ext.endsWith('.xlsx')) return '📊';
    if (ext.endsWith('.ppt') || ext.endsWith('.pptx')) return '📽️';
    if (ext.endsWith('.zip') || ext.endsWith('.rar') || ext.endsWith('.7z')) return '📦';
    if (ext.endsWith('.mp4') || ext.endsWith('.avi') || ext.endsWith('.mov')) return '🎬';
    if (ext.endsWith('.mp3') || ext.endsWith('.wav') || ext.endsWith('.flac')) return '🎵';
    if (ext.endsWith('.txt')) return '📃';
    if (ext.endsWith('.html') || ext.endsWith('.css') || ext.endsWith('.js')) return '💻';
    if (is3DModelFile(ext)) return '🧊'; // 3D 모델 아이콘
    return '📄';
  };

  // 파일 검색 및 필터링
  const filterFiles = (fileList) => {
    if (!searchQuery.trim()) {
      return fileList;
    }

    const query = searchQuery.toLowerCase();
    return fileList.filter(file => {
      // 파일명으로 검색
      const nameMatch = file.name.toLowerCase().includes(query);

      // 원본 파일명으로 검색
      const originalNameMatch = file.metadata?.['original-filename']?.toLowerCase().includes(query);

      // 업로드 출처로 검색
      const originMatch = file.metadata?.['upload-origin']?.toLowerCase().includes(query);

      return nameMatch || originalNameMatch || originMatch;
    });
  };

  // 파일 정렬
  const sortFiles = (fileList) => {
    // sortBy가 null이면 정렬하지 않고 원래 순서 유지 (S3 순서)
    if (!sortBy) {
      return fileList;
    }

    return [...fileList].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'size':
          comparison = a.size - b.size;
          break;
        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });
  };

  // 검색 후 정렬 적용
  const getProcessedFiles = () => {
    const filtered = filterFiles(files);
    const sorted = sortFiles(filtered);

    if (isPaginationEnabled) {
      // 페이지 번호 모드: 현재 페이지에 해당하는 파일만 반환
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      return sorted.slice(startIndex, endIndex);
    } else {
      // 더보기 모드: displayedFileCount만큼 반환
      return sorted.slice(0, displayedFileCount);
    }
  };

  // 전체 파일 개수 (페이징 전)
  const getTotalFileCount = () => {
    const filtered = filterFiles(files);
    return filtered.length;
  };

  // 전체 페이지 수
  const getTotalPages = () => {
    return Math.ceil(getTotalFileCount() / ITEMS_PER_PAGE);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 더보기 버튼 핸들러
  const handleLoadMore = () => {
    setDisplayedFileCount(prev => prev + ITEMS_PER_PAGE);
  };

  // 페이징 토글 핸들러
  const handleTogglePagination = () => {
    setIsPaginationEnabled(prev => !prev);
    setCurrentPage(1);
    setDisplayedFileCount(ITEMS_PER_PAGE);
  };

  // 화면에 표시되는 GLB 파일들의 썸네일 자동 로드
  useEffect(() => {
    const visibleFiles = getProcessedFiles();
    const glbFiles = visibleFiles.filter(file => isGLBFile(file.name));

    // GLB 파일들의 썸네일을 완전히 순차적으로 로드 (동시 처리 방지)
    const loadThumbnails = async () => {
      for (let i = 0; i < glbFiles.length; i++) {
        await loadGLBThumbnail(glbFiles[i]);
        // 각 썸네일 생성 후 약간의 대기 시간 (메모리 정리)
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    };

    loadThumbnails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, currentPage, displayedFileCount, isPaginationEnabled, sortBy, sortOrder, searchQuery]);

  // 정렬 토글
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'date' ? 'desc' : 'asc'); // 날짜는 기본 내림차순
    }
  };

  // 파일 선택 토글
  const handleFileSelect = (file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.key === file.key);
      if (isSelected) {
        return prev.filter(f => f.key !== file.key);
      } else {
        return [...prev, file];
      }
    });
  };

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedFiles.length === files.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(files.map(f => ({ key: currentPath ? `${currentPath}/${f.name}` : f.name, filename: f.name })));
    }
  };

  // 단일 파일 다운로드
  const handleDownloadFile = async (file) => {
    try {
      const key = currentPath ? `${currentPath}/${file.name}` : file.name;
      const response = await fetch(`${getApiBase()}/download-url?key=${encodeURIComponent(key)}&filename=${encodeURIComponent(file.name)}`);
      const data = await response.json();

      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('다운로드 실패');
    }
  };

  // 선택 파일 ZIP 다운로드
  const handleDownloadSelected = async () => {
    if (selectedFiles.length === 0) {
      alert('다운로드할 파일을 선택해주세요');
      return;
    }

    setIsDownloading(true);
    try {
      const response = await fetch(`${getApiBase()}/download-selected-zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keys: selectedFiles,
          zipName: `선택파일_${new Date().toISOString().slice(0,10)}.zip`
        })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `선택파일_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('ZIP download error:', err);
      alert('ZIP 다운로드 실패');
    } finally {
      setIsDownloading(false);
    }
  };

  // 현재 폴더 전체 ZIP 다운로드
  const handleDownloadFolder = async () => {
    try {
      // 먼저 폴더 용량 확인
      const folderParam = currentPath ? `folder=${encodeURIComponent(currentPath)}` : '';
      const infoResponse = await fetch(`${getApiBase()}/bucket-info?${folderParam}`);
      const info = await infoResponse.json();

      // 2GB 이상이면 분할 다운로드 안내
      if (info.totalSize > 2 * 1024 * 1024 * 1024) {
        const useSplit = window.confirm(
          `폴더가 너무 큽니다 (${info.totalSizeFormatted}).\n\n` +
          `분할 다운로드를 사용하시겠습니까?`
        );

        if (useSplit) {
          handleShowSplitDownload();
          return;
        } else {
          return;
        }
      }

      const confirmMsg = `폴더를 다운로드합니다.\n\n` +
        `총 파일 수: ${info.totalFiles.toLocaleString()}개\n` +
        `총 용량: ${info.totalSizeFormatted}\n\n` +
        `계속하시겠습니까?`;

      if (!window.confirm(confirmMsg)) {
        return;
      }

      setIsDownloading(true);

      const folderPathParam = currentPath ? `folder=${encodeURIComponent(currentPath)}/` : '';
      const zipName = currentPath ? currentPath.split('/').pop() : 'all_files';

      const response = await fetch(`${getApiBase()}/download-zip?${folderPathParam}&zipName=${encodeURIComponent(zipName)}.zip`);

      if (response.status === 413) {
        const error = await response.json();
        alert(`폴더가 너무 큽니다: ${error.totalSizeFormatted}\n분할 다운로드를 사용하세요.`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${zipName}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Folder ZIP download error:', err);
      alert('폴더 다운로드 실패');
    } finally {
      setIsDownloading(false);
    }
  };

  // 분할 다운로드 정보 가져오기
  const handleShowSplitDownload = async () => {
    try {
      const folderParam = currentPath ? `folder=${encodeURIComponent(currentPath)}` : '';
      const response = await fetch(`${getApiBase()}/split-download-info?${folderParam}`);
      const info = await response.json();

      if (response.status === 404) {
        alert('다운로드할 파일이 없습니다.');
        return;
      }

      setSplitInfo(info);
      setShowSplitDownload(true);
    } catch (err) {
      console.error('Error fetching split info:', err);
      alert('분할 다운로드 정보를 가져오는데 실패했습니다.');
    }
  };

  // 특정 분할 다운로드
  const handleDownloadSplit = async (split) => {
    try {
      setIsDownloading(true);
      setDownloadProgress({ current: split.index + 1, total: 1, status: 'downloading' });

      const response = await fetch(`${getApiBase()}/download-split-zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folder: currentPath,
          splitIndex: split.index,
          fileKeys: split.files.map(f => f.key),
          zipName: `backup_part${split.index + 1}_${new Date().toISOString().slice(0,10)}.zip`
        })
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backup_part${split.index + 1}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);

      setDownloadProgress({ current: split.index + 1, total: 1, status: 'completed' });
    } catch (err) {
      console.error('Split download error:', err);
      setDownloadProgress({ current: split.index + 1, total: 1, status: 'error' });
      alert('분할 다운로드 실패');
    } finally {
      setIsDownloading(false);
      setTimeout(() => setDownloadProgress(null), 2000);
    }
  };

  // 모든 분할 순차 다운로드
  const handleDownloadAllSplits = async () => {
    if (!splitInfo || !splitInfo.splits) return;

    const confirmMsg = `${splitInfo.splitCount}개의 ZIP 파일로 순차 다운로드합니다.\n\n` +
      `총 파일 수: ${splitInfo.totalFiles.toLocaleString()}개\n` +
      `총 용량: ${splitInfo.totalSizeFormatted}\n\n` +
      `계속하시겠습니까?`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsDownloading(true);

    for (let i = 0; i < splitInfo.splits.length; i++) {
      const split = splitInfo.splits[i];

      try {
        setDownloadProgress({ current: i + 1, total: splitInfo.splits.length, status: 'downloading' });

        // 분할 정보를 다시 가져와서 파일 키 포함
        const folderParam = currentPath ? `folder=${encodeURIComponent(currentPath)}` : '';
        const infoResponse = await fetch(`${getApiBase()}/split-download-info?${folderParam}`);
        const fullInfo = await infoResponse.json();
        const fullSplit = fullInfo.splits[i];

        const response = await fetch(`${getApiBase()}/download-split-zip`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            folder: currentPath,
            splitIndex: fullSplit.index,
            fileKeys: fullSplit.files.map(f => f.key),
            zipName: `backup_part${i + 1}_${new Date().toISOString().slice(0,10)}.zip`
          })
        });

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup_part${i + 1}.zip`;
        a.click();
        window.URL.revokeObjectURL(url);

        setDownloadProgress({ current: i + 1, total: splitInfo.splits.length, status: 'completed' });

        // 다음 다운로드 전 잠시 대기
        if (i < splitInfo.splits.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (err) {
        console.error(`Split ${i + 1} download error:`, err);
        setDownloadProgress({ current: i + 1, total: splitInfo.splits.length, status: 'error' });
        if (!window.confirm(`파트 ${i + 1} 다운로드 실패. 계속하시겠습니까?`)) {
          break;
        }
      }
    }

    setIsDownloading(false);
    setDownloadProgress(null);
    alert('모든 분할 다운로드가 완료되었습니다.');
  };

  // 버킷 전체 백업 다운로드 (2GB 이하인 경우만)
  const handleDownloadAll = async () => {
    try {
      // 먼저 전체 용량 확인
      const infoResponse = await fetch(`${getApiBase()}/bucket-info`);
      const info = await infoResponse.json();

      // 2GB 이상이면 분할 다운로드 안내
      if (info.totalSize > 2 * 1024 * 1024 * 1024) {
        const useSlpit = window.confirm(
          `파일이 너무 큽니다 (${info.totalSizeFormatted}).\n\n` +
          `분할 다운로드를 사용하시겠습니까?`
        );

        if (useSlpit) {
          handleShowSplitDownload();
          return;
        } else {
          return;
        }
      }

      const confirmMsg = `전체 백업을 다운로드합니다.\n\n` +
        `총 파일 수: ${info.totalFiles.toLocaleString()}개\n` +
        `총 용량: ${info.totalSizeFormatted}\n\n` +
        `계속하시겠습니까?`;

      if (!window.confirm(confirmMsg)) {
        return;
      }

      setIsDownloading(true);

      const response = await fetch(`${getApiBase()}/download-zip?zipName=전체백업_${new Date().toISOString().slice(0,10)}.zip`);

      if (response.status === 413) {
        const error = await response.json();
        alert(`파일이 너무 큽니다: ${error.totalSizeFormatted}\n분할 다운로드를 사용하세요.`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `전체백업_${new Date().toISOString().slice(0,10)}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Full backup download error:', err);
      alert('전체 백업 다운로드 실패');
    } finally {
      setIsDownloading(false);
    }
  };

  // 검색/정렬/필터링된 파일 목록
  const processedFiles = getProcessedFiles();
  const totalFiles = getTotalFileCount();
  const totalPages = getTotalPages();

  // 페이지 번호 윈도우 계산
  const pageWindow = useMemo(() => {
    const tp = totalPages;
    if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
    let s = Math.max(2, currentPage - 2);
    let e = Math.min(tp - 1, currentPage + 2);
    if (currentPage <= 4) { s = 2; e = 5; }
    if (currentPage >= tp - 3) { s = tp - 4; e = tp - 1; }
    const arr = [1];
    if (s > 2) arr.push('...');
    for (let i = s; i <= e; i++) arr.push(i);
    if (e < tp - 1) arr.push('...');
    arr.push(tp);
    return arr;
  }, [currentPage, totalPages]);

  // 파일 타입별 아이콘 (테이블용)
  const renderFileIcon = (file) => {
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (isImageFile(file.name)) {
      const failed = failedImages.has(file.url);
      if (!failed) {
        return (
          <img
            src={file.url}
            alt={file.name}
            onError={() => setFailedImages((prev) => new Set(prev).add(file.url))}
          />
        );
      }
    }
    if (is3DModelFile(file.name)) {
      const thumb = glbThumbnails[file.key];
      if (thumb) return <img src={thumb} alt={file.name} />;
      return <Generic3DSvg />;
    }
    if (ext === 'pdf') return <PdfSvg />;
    if (['doc', 'docx', 'hwp'].includes(ext)) return <DocSvg />;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return <SheetSvg />;
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return <ZipSvg />;
    return <GenericFileSvg />;
  };

  // 파일 타입별 박스 컬러
  const getThumbBg = (file) => {
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (isImageFile(file.name)) return '#f3f4f6';
    if (is3DModelFile(file.name)) return '#f5f3ff';
    if (ext === 'pdf') return '#fef2f2';
    if (['doc', 'docx', 'hwp'].includes(ext)) return '#eff6ff';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '#dcfce7';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return '#fef3c7';
    return '#f3f4f6';
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('ko-KR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).replace(/\. /g, '. ');
    } catch { return iso; }
  };

  const segs = (currentPath || '').split('/').filter(Boolean);
  const allSelected = files.length > 0 && selectedFiles.length === files.length;
  const partialSelected = selectedFiles.length > 0 && selectedFiles.length < files.length;

  return (
    <NewPage>
      {!selectedRemote ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4rem',
          gap: '1rem',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          margin: '2rem auto',
          maxWidth: '600px'
        }}>
          <div style={{ fontSize: '4rem' }}>📡</div>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#374151'
          }}>원격지를 선택해주세요</div>
          <div style={{
            fontSize: '1rem',
            color: '#6b7280',
            textAlign: 'center'
          }}>
            상단 드롭다운에서 연결할 원격지를 선택하면<br/>
            파일 관리를 시작할 수 있습니다.
          </div>
        </div>
      ) : (
        <>
          <Breadcrumb>
            <CrumbBtn onClick={() => setCurrentPath('')}><HomeSvg /></CrumbBtn>
            {segs.length === 0 && <span className="sep">/</span>}
            {segs.map((seg, idx) => {
              const path = segs.slice(0, idx + 1).join('/');
              const isLast = idx === segs.length - 1;
              return (
                <React.Fragment key={path}>
                  <span className="sep">/</span>
                  <CrumbBtn active={isLast ? 1 : 0} onClick={() => setCurrentPath(path)}>{seg}</CrumbBtn>
                </React.Fragment>
              );
            })}
          </Breadcrumb>

          <Toolbar>
            <SelectionLabel>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = partialSelected; }}
                onChange={handleSelectAll}
              />
              선택 {selectedFiles.length}개
            </SelectionLabel>
            <ToolBtn
              onClick={handleDownloadSelected}
              disabled={selectedFiles.length === 0 || isDownloading}
            >
              <DownloadSvg /> 다운로드 <ChevronDownSvg />
            </ToolBtn>
            <ToolBtn
              onClick={handleDownloadFolder}
              disabled={isDownloading || files.length === 0}
            >
              <DownloadSvg /> 폴더 전체 ZIP
            </ToolBtn>
            <ToolBtn tone="danger" onClick={handleDownloadAll} disabled={isDownloading}>
              <DownloadSvg /> 전체 백업 <ChevronDownSvg />
            </ToolBtn>
            <ToolBtn onClick={handleShowSplitDownload} disabled={isDownloading}>
              분할 다운로드
            </ToolBtn>
            <Spacer />
            <ToolBtn onClick={handleTogglePagination}>
              더보기 <ChevronDownSvg />
            </ToolBtn>
          </Toolbar>

          {isLoadingFiles && <TopProgress />}

          <ViewBar>
            <ViewToggleGroup>
              <ViewToggleBtn active={viewMode === 'list' ? 1 : 0} onClick={() => setViewMode('list')} aria-label="리스트">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              </ViewToggleBtn>
              <ViewToggleBtn active={viewMode === 'grid' ? 1 : 0} onClick={() => setViewMode('grid')} aria-label="그리드">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
              </ViewToggleBtn>
            </ViewToggleGroup>
          </ViewBar>

          {error && (
            <div style={{ padding: '1rem', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px' }}>
              {error}
            </div>
          )}

          {isLoadingFiles && (
            <LoadingHero>
              <BigSpinner />
              <LoadingHeroTitle>파일을 불러오는 중...</LoadingHeroTitle>
              <LoadingHeroSub>
                {selectedRemote?.name}{currentPath ? ` / ${currentPath}` : ''} 의 파일 목록을 가져오고 있습니다.
              </LoadingHeroSub>
            </LoadingHero>
          )}

          {!isLoadingFiles && <TableCard>
            <TableEl>
              <thead>
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => { if (el) el.indeterminate = partialSelected; }}
                      onChange={handleSelectAll}
                      style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                    />
                  </Th>
                  <Th sortable={1} active={sortBy === 'name' ? 1 : 0} onClick={() => handleSort('name')}>
                    이름 <span className="arrow">{sortBy === 'name' ? (sortOrder === 'asc' ? '↑' : '↓') : '⇅'}</span>
                  </Th>
                  <Th sortable={1} active={sortBy === 'size' ? 1 : 0} onClick={() => handleSort('size')}>
                    크기 <span className="arrow">{sortBy === 'size' ? (sortOrder === 'asc' ? '↑' : '↓') : '⇅'}</span>
                  </Th>
                  <Th sortable={1} active={sortBy === 'date' ? 1 : 0} onClick={() => handleSort('date')}>
                    업로드 날짜 <span className="arrow">{sortBy === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : '⇅'}</span>
                  </Th>
                  <Th>출처 / 상태</Th>
                  <Th>작업</Th>
                </tr>
              </thead>
              <tbody>
                {(
                  <>
                    {currentPath && (
                      <RowTr onClick={handleBackClick} style={{ cursor: 'pointer' }}>
                        <Td></Td>
                        <Td colSpan={5}>
                          <FileNameCell>
                            <FileThumb bg="#fef9c3" color="#a16207"><FolderYellowSvg /></FileThumb>
                            <FileNameInfo><FileNameText>.. (상위 폴더로)</FileNameText></FileNameInfo>
                          </FileNameCell>
                        </Td>
                      </RowTr>
                    )}
                    {folders.map((folder) => (
                      <RowTr key={`folder-${folder}`} onClick={() => handleFolderClick(folder)} style={{ cursor: 'pointer' }}>
                        <Td onClick={(e) => e.stopPropagation()}></Td>
                        <Td>
                          <FileNameCell>
                            <FileThumb bg="#fef9c3"><FolderYellowSvg /></FileThumb>
                            <FileNameInfo><FileNameText>{folder}</FileNameText></FileNameInfo>
                          </FileNameCell>
                        </Td>
                        <Td>-</Td>
                        <Td>-</Td>
                        <Td><SourcePill>폴더</SourcePill></Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <RowActions><IconActionBtn aria-label="more"><MoreSvg /></IconActionBtn></RowActions>
                        </Td>
                      </RowTr>
                    ))}
                    {processedFiles.length === 0 && folders.length === 0 && !currentPath ? (
                      <tr><td colSpan={6}><Empty2>파일이 없습니다.</Empty2></td></tr>
                    ) : (
                      processedFiles.map((file) => {
                        const isSelected = selectedFiles.some((f) => f.name === file.name);
                        const origin = file.metadata?.['upload-origin'];
                        const ip = file.metadata?.['upload-ip'];
                        return (
                          <RowTr key={file.key || file.name}>
                            <Td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleFileSelect(file)}
                                style={{ accentColor: '#2563eb', cursor: 'pointer' }}
                              />
                            </Td>
                            <Td>
                              <FileNameCell>
                                <FileThumb bg={getThumbBg(file)}>{renderFileIcon(file)}</FileThumb>
                                <FileNameInfo>
                                  <FileNameText title={file.name}>{file.name}</FileNameText>
                                  <FileUrlText href={file.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                                    {file.url}
                                  </FileUrlText>
                                </FileNameInfo>
                              </FileNameCell>
                            </Td>
                            <Td>{formatSize(file.size)}</Td>
                            <Td>{formatDate(file.createdAt)}</Td>
                            <Td>
                              <SourcePill tone={origin ? 'success' : 'default'}>
                                {origin || '출처 정보 없음'}
                              </SourcePill>
                              <SourcePill tone="warn">IP: {ip || '정보 없음'}</SourcePill>
                            </Td>
                            <Td>
                              <RowActions>
                                <IconActionBtn onClick={() => handleDownloadFile(file)} aria-label="다운로드"><DownloadSvg /></IconActionBtn>
                                <IconActionBtn aria-label="more"><MoreSvg /></IconActionBtn>
                              </RowActions>
                            </Td>
                          </RowTr>
                        );
                      })
                    )}
                  </>
                )}
              </tbody>
            </TableEl>

            <PaginationBar>
              <PageInfo>전체 {totalFiles.toLocaleString()}개 파일</PageInfo>
              {isPaginationEnabled ? (
                <PageNums>
                  <PageNavBtn onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                    <ChevronLeftSvg />
                  </PageNavBtn>
                  {pageWindow.map((p, i) =>
                    p === '...' ? (
                      <Ellipsis key={`e-${i}`}>...</Ellipsis>
                    ) : (
                      <PageNumBtn key={p} active={p === currentPage ? 1 : 0} onClick={() => handlePageChange(p)}>
                        {p}
                      </PageNumBtn>
                    )
                  )}
                  <PageNavBtn onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
                    <ChevronRightSvg />
                  </PageNavBtn>
                </PageNums>
              ) : (
                <PageNumBtn onClick={handleLoadMore} disabled={displayedFileCount >= totalFiles}>
                  더보기
                </PageNumBtn>
              )}
              <PerPageSelect value={isPaginationEnabled ? 'page' : 'more'} onChange={handleTogglePagination}>
                <option value="page">페이지 보기</option>
                <option value="more">더보기 모드</option>
              </PerPageSelect>
            </PaginationBar>
          </TableCard>}
        </>

      )}

      {/* 분할 다운로드 모달 */}
      {showSplitDownload && splitInfo && (
        <Modal onClick={() => setShowSplitDownload(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>분할 다운로드</ModalTitle>
              <CloseButton onClick={() => setShowSplitDownload(false)}>×</CloseButton>
            </ModalHeader>

            {/* 진행 상태 표시 */}
            {downloadProgress && (
              <ProgressContainer>
                <ProgressText>
                  {downloadProgress.status === 'downloading' && `파트 ${downloadProgress.current} / ${downloadProgress.total} 다운로드 중...`}
                  {downloadProgress.status === 'completed' && `파트 ${downloadProgress.current} / ${downloadProgress.total} 완료!`}
                  {downloadProgress.status === 'error' && `파트 ${downloadProgress.current} / ${downloadProgress.total} 오류 발생`}
                </ProgressText>
                <ProgressBarContainer>
                  <ProgressBar
                    status={downloadProgress.status}
                    style={{ width: `${(downloadProgress.current / downloadProgress.total) * 100}%` }}
                  >
                    {Math.round((downloadProgress.current / downloadProgress.total) * 100)}%
                  </ProgressBar>
                </ProgressBarContainer>
              </ProgressContainer>
            )}

            <InfoBox>
              <InfoRow>
                <InfoLabel>총 파일 수:</InfoLabel>
                <InfoValue>{splitInfo.totalFiles.toLocaleString()}개</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>총 용량:</InfoLabel>
                <InfoValue>{splitInfo.totalSizeFormatted}</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>분할 개수:</InfoLabel>
                <InfoValue>{splitInfo.splitCount}개 (각 약 1GB)</InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel>현재 경로:</InfoLabel>
                <InfoValue>{splitInfo.folder}</InfoValue>
              </InfoRow>
            </InfoBox>

            <ButtonGroup style={{ justifyContent: 'center' }}>
              <ActionButton
                primary
                onClick={handleDownloadAllSplits}
                disabled={isDownloading}
                style={{ fontSize: '1rem', padding: '0.75rem 1.5rem' }}
              >
                {isDownloading ? '다운로드 중...' : '모든 분할 순차 다운로드'}
              </ActionButton>
            </ButtonGroup>

            <div style={{ margin: '1.5rem 0', textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>
              또는 개별 다운로드
            </div>

            <SplitList>
              {splitInfo.splits.map((split, index) => (
                <SplitItem key={index}>
                  <SplitInfo>
                    <SplitTitle>파트 {split.index + 1}</SplitTitle>
                    <SplitDetails>
                      {split.fileCount.toLocaleString()}개 파일 · {split.sizeFormatted}
                    </SplitDetails>
                  </SplitInfo>
                  <ActionButton
                    onClick={() => handleDownloadSplit(split)}
                    disabled={isDownloading}
                  >
                    {isDownloading ? '다운로드 중...' : '다운로드'}
                  </ActionButton>
                </SplitItem>
              ))}
            </SplitList>
          </ModalContent>
        </Modal>
      )}

      {/* 메타데이터 모달 */}
      {metadataModalOpen && selectedFileMetadata && (
        <Modal onClick={() => setMetadataModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>파일 메타데이터</ModalTitle>
              <CloseButton onClick={() => setMetadataModalOpen(false)}>×</CloseButton>
            </ModalHeader>

            {/* GLB 썸네일 미리보기 */}
            {isGLBFile(selectedFileMetadata.name) && glbThumbnails[selectedFileMetadata.key] && (
              <ThumbnailPreview
                src={glbThumbnails[selectedFileMetadata.key]}
                alt={selectedFileMetadata.name}
              />
            )}

            {/* 이미지 파일 미리보기 */}
            {isImageFile(selectedFileMetadata.name) && (
              <ThumbnailPreview
                src={selectedFileMetadata.url}
                alt={selectedFileMetadata.name}
              />
            )}

            <div>
              <MetadataRow>
                <MetadataLabel>파일명</MetadataLabel>
                <MetadataValue>{selectedFileMetadata.name}</MetadataValue>
              </MetadataRow>

              <MetadataRow>
                <MetadataLabel>S3 키</MetadataLabel>
                <MetadataValue>{selectedFileMetadata.key}</MetadataValue>
              </MetadataRow>

              <MetadataRow>
                <MetadataLabel>파일 크기</MetadataLabel>
                <MetadataValue>{formatSize(selectedFileMetadata.size)}</MetadataValue>
              </MetadataRow>

              <MetadataRow>
                <MetadataLabel>생성 날짜</MetadataLabel>
                <MetadataValue>{new Date(selectedFileMetadata.createdAt).toLocaleString('ko-KR')}</MetadataValue>
              </MetadataRow>

              <MetadataRow>
                <MetadataLabel>파일 URL</MetadataLabel>
                <MetadataValue>
                  <a href={selectedFileMetadata.url} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                    {selectedFileMetadata.url}
                  </a>
                </MetadataValue>
              </MetadataRow>

              {/* 업로드 메타데이터 */}
              {selectedFileMetadata.metadata && Object.keys(selectedFileMetadata.metadata).length > 0 && (
                <>
                  <MetadataRow style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e5e7eb' }}>
                    <MetadataLabel style={{ fontSize: '1rem', fontWeight: '700' }}>업로드 정보</MetadataLabel>
                  </MetadataRow>

                  {selectedFileMetadata.metadata['upload-origin'] && (
                    <MetadataRow>
                      <MetadataLabel>업로드 출처</MetadataLabel>
                      <MetadataValue>{selectedFileMetadata.metadata['upload-origin']}</MetadataValue>
                    </MetadataRow>
                  )}

                  {selectedFileMetadata.metadata['upload-ip'] && (
                    <MetadataRow>
                      <MetadataLabel>업로드 IP</MetadataLabel>
                      <MetadataValue>{selectedFileMetadata.metadata['upload-ip']}</MetadataValue>
                    </MetadataRow>
                  )}

                  {selectedFileMetadata.metadata['user-agent'] && (
                    <MetadataRow>
                      <MetadataLabel>User Agent</MetadataLabel>
                      <MetadataValue>{selectedFileMetadata.metadata['user-agent']}</MetadataValue>
                    </MetadataRow>
                  )}

                  {selectedFileMetadata.metadata['upload-time'] && (
                    <MetadataRow>
                      <MetadataLabel>업로드 시간</MetadataLabel>
                      <MetadataValue>{new Date(selectedFileMetadata.metadata['upload-time']).toLocaleString('ko-KR')}</MetadataValue>
                    </MetadataRow>
                  )}

                  {/* 기타 메타데이터 출력 */}
                  {Object.entries(selectedFileMetadata.metadata)
                    .filter(([key]) => !['upload-origin', 'upload-ip', 'user-agent', 'upload-time'].includes(key))
                    .map(([key, value]) => (
                      <MetadataRow key={key}>
                        <MetadataLabel>{key}</MetadataLabel>
                        <MetadataValue>{value}</MetadataValue>
                      </MetadataRow>
                    ))}
                </>
              )}
            </div>
          </ModalContent>
        </Modal>
      )}
    </NewPage>
  );
};

export default FileView;